import React, { useEffect, useState, useMemo } from "react";
import {
  Pagination,
  Form,
  Table,
  Container,
  Row,
  Col,
  Image,
  Button,
  Modal,
  InputGroup,
  Card,
  Badge,
  Spinner,
  Dropdown,
  DropdownButton,
  Tabs,
  Tab,
  Nav,
  Alert,
  FormControl,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminHeader from "../../../Components/AdminHeader";
import Sidebar from "../../../Components/Sidebar";
import { 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaTimes, 
  FaCar, 
  FaBuilding, 
  FaTag, 
  FaImage, 
  FaVideo,
  FaDollarSign,
  FaCheck,
  FaInfoCircle,
  FaFilter,
  FaSave,
  FaEye,
  FaExclamationTriangle
} from "react-icons/fa";

const API_BASE_URL = "https://motor-easy.clickbooster.io/api";

const AllProducts = () => {
  // Main data state
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("allCars");
  
  // Modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  // Dropdown state management
  const [carsList, setCarsList] = useState([]);
  const [makersList, setMakersList] = useState([]);
  const [modelsList, setModelsList] = useState([]);
  const [selectedCar, setSelectedCar] = useState("");
  const [selectedMaker, setSelectedMaker] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [needsDropdownUpdate, setNeedsDropdownUpdate] = useState(false);

  // For custom input options
  const [useCustomCar, setUseCustomCar] = useState(false);
  const [useCustomMaker, setUseCustomMaker] = useState(false);
  const [useCustomModel, setUseCustomModel] = useState(false);
  const [customCarName, setCustomCarName] = useState("");
  const [customMakerName, setCustomMakerName] = useState("");
  const [customModelName, setCustomModelName] = useState("");

  // State for form data
  const [formData, setFormData] = useState({
    car_name: "",
    maker_name: "",
    model_name: "",
    car_type: "",
    engine_capacity: "",
    service_type: "",
    product_code: "",
    head_title: "",
    product_desc: "",
    short_des: "",
    d_title: "",
    d_feature: "",
    prices: [{ plan_name: "", price: "", plan_duration: "" }],
  });

  // Media state
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  // Initial data fetching
  useEffect(() => {
    fetchData();
    fetchCarsList();
  }, []);

  // Fetch all cars for the main table
  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetch(`${API_BASE_URL}/new-entries`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setCars(data.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load cars data. Please try again later.");
      toast.error("Failed to load cars data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all cars for dropdown
  const fetchCarsList = async () => {
    try {
      setDataLoading(true);
      const response = await fetch(`${API_BASE_URL}/cars`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setCarsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching car data:", error);
      toast.error("Failed to load car options");
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch makers based on selected car
  useEffect(() => {
    const fetchMakers = async () => {
      if (!selectedCar) {
        setMakersList([]);
        return;
      }

      try {
        setDataLoading(true);
        const response = await fetch(`${API_BASE_URL}/maker?car_id=${selectedCar}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setMakersList(data && Array.isArray(data.makers) ? data.makers : []);
      } catch (error) {
        console.error("Error fetching makers:", error);
        toast.error("Failed to load maker options");
      } finally {
        setDataLoading(false);
      }
    };

    fetchMakers();
  }, [selectedCar]);

  // Fetch models based on selected car and maker
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedCar || !selectedMaker) {
        setModelsList([]);
        return;
      }

      try {
        setDataLoading(true);
        const response = await fetch(`${API_BASE_URL}/models?car_id=${selectedCar}&maker_id=${selectedMaker}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setModelsList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching models:", error);
        toast.error("Failed to load model options");
      } finally {
        setDataLoading(false);
      }
    };

    fetchModels();
  }, [selectedCar, selectedMaker]);

  // Search functionality
  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.toLowerCase());
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter cars based on search term
  const filteredCars = useMemo(() => {
    if (!searchTerm) return cars;

    return cars.filter((car) => {
      // Check if car exists and has the expected properties
      if (!car) return false;
      
      // Add null checks for all properties
      const carName = car?.cars?.car_name?.toLowerCase() || "";
      const carId = car?.id?.toString() || "";
      const makerName = car?.makers?.maker_name?.toLowerCase() || "";
      const modelName = car?.models?.model_name?.toLowerCase() || "";
      const carType = car?.car_type?.toLowerCase() || "";
      const engineCapacity = car?.engine_capacity?.toLowerCase() || "";
      const serviceType = car?.service_type?.toLowerCase() || "";
      const productCode = car?.product_code?.toLowerCase() || "";
      
      return (
        carName.includes(searchTerm) ||
        carId.includes(searchTerm) ||
        makerName.includes(searchTerm) ||
        modelName.includes(searchTerm) ||
        carType.includes(searchTerm) ||
        engineCapacity.includes(searchTerm) ||
        serviceType.includes(searchTerm) ||
        productCode.includes(searchTerm)
      );
    });
  }, [cars, searchTerm]);

  // Pagination functionality
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.ceil(filteredCars.length / itemsPerPage);
  const displayedCars = filteredCars.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Delete car functionality
  const handleShowDeleteConfirm = (car) => {
    setItemToDelete(car);
    setDeleteType("car");
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/new-entries/delete/${itemToDelete.id}`, {
        method: "DELETE",
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      toast.success("Vehicle deleted successfully");
      setCars(cars.filter((car) => car.id !== itemToDelete.id));
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting data:", error);
      toast.error("Failed to delete vehicle");
    } finally {
      setLoading(false);
    }
  };

  // Media handling functions
  const handleRemoveExistingMedia = async (type, id) => {
    try {
      setDataLoading(true);
      const response = await fetch(`${API_BASE_URL}/delete${type}ById/${id}`, {
        method: "DELETE",
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      toast.success(`${type} deleted successfully`);
      
      if (type === "Image") {
        setImages(prevImages => prevImages.filter(img => img.id !== id));
      } else if (type === "Video") {
        setVideos(prevVideos => prevVideos.filter(vid => vid.id !== id));
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      toast.error(`Failed to delete ${type}`);
    } finally {
      setDataLoading(false);
    }
  };

  const handleFileChange = (e, setFiles) => {
    const files = Array.from(e.target.files);

    if (e.target.name === "images") {
      const validImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const validFiles = files.filter(file => validImageTypes.includes(file.type));

      if (validFiles.length !== files.length) {
        toast.error("Some files were not valid images. Only JPEG, PNG, JPG, and GIF are allowed.");
      }

      setFiles(validFiles);
      
      // Create preview URLs for the images
      const previews = validFiles.map(file => URL.createObjectURL(file));
      setPreviewImages(previews);
    } else if (e.target.name === "videos") {
      const validVideoTypes = ["video/mp4", "video/mov", "video/avi", "video/wmv"];
      const validFiles = files.filter(file => validVideoTypes.includes(file.type));

      if (validFiles.length !== files.length) {
        toast.error("Some files were not valid videos. Only MP4, MOV, AVI, and WMV are allowed.");
      }

      setFiles(validFiles);
    }
  };

  // Form management functions
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleArrayChange = (e, index, arrayName) => {
    const { name, value } = e.target;
    const updatedArray = [...formData[arrayName]];
    updatedArray[index] = {
      ...updatedArray[index],
      [name]: value.trim(),
    };
    setFormData(prevData => ({
      ...prevData,
      [arrayName]: updatedArray,
    }));
  };

  const addArrayField = (arrayName) => {
    setFormData(prevData => ({
      ...prevData,
      [arrayName]: [...prevData[arrayName], { plan_name: "", price: "", plan_duration: "" }],
    }));
  };

  const removeArrayField = (arrayName, index) => {
    if (formData[arrayName].length <= 1) {
      // Keep at least one field
      return;
    }
    
    setFormData(prevData => {
      const newArray = [...prevData[arrayName]];
      newArray.splice(index, 1);
      return {
        ...prevData,
        [arrayName]: newArray
      };
    });
  };

  // Custom input handlers
  const handleCustomCarNameChange = (e) => {
    const value = e.target.value;
    setCustomCarName(value);
    setFormData(prevData => ({
      ...prevData,
      car_name: value,
    }));
  };

  const handleCustomMakerNameChange = (e) => {
    const value = e.target.value;
    setCustomMakerName(value);
    setFormData(prevData => ({
      ...prevData,
      maker_name: value,
    }));
  };

  const handleCustomModelNameChange = (e) => {
    const value = e.target.value;
    setCustomModelName(value);
    setFormData(prevData => ({
      ...prevData,
      model_name: value,
    }));
  };

  // Toggle custom input fields
  const toggleCustomCar = () => {
    const newState = !useCustomCar;
    setUseCustomCar(newState);

    if (newState) {
      // Switching to custom input - use existing name if available
      if (formData.car_name) {
        setCustomCarName(formData.car_name);
      }
    } else {
      // Switching to dropdown - find matching car if possible
      const selectedCarObj = carsList.find(
        car => car && car.car_name.toLowerCase() === customCarName.toLowerCase()
      );
      if (selectedCarObj) {
        setSelectedCar(selectedCarObj.id);
      } else {
        setSelectedCar("");
      }
    }
  };

  const toggleCustomMaker = () => {
    const newState = !useCustomMaker;
    setUseCustomMaker(newState);

    if (newState) {
      // Switching to custom input - use existing name if available
      if (formData.maker_name) {
        setCustomMakerName(formData.maker_name);
      }
    } else {
      // Switching to dropdown - find matching maker if possible
      const selectedMakerObj = makersList.find(
        maker => maker && maker.maker_name.toLowerCase() === customMakerName.toLowerCase()
      );
      if (selectedMakerObj) {
        setSelectedMaker(selectedMakerObj.id);
      } else {
        setSelectedMaker("");
      }
    }
  };

  const toggleCustomModel = () => {
    const newState = !useCustomModel;
    setUseCustomModel(newState);

    if (newState) {
      // Switching to custom input - use existing name if available
      if (formData.model_name) {
        setCustomModelName(formData.model_name);
      }
    } else {
      // Switching to dropdown - find matching model if possible
      const selectedModelObj = modelsList.find(
        model => model && model.model_name.toLowerCase() === customModelName.toLowerCase()
      );
      if (selectedModelObj) {
        setSelectedModel(selectedModelObj.id);
      } else {
        setSelectedModel("");
      }
    }
  };

  // Dropdown handlers
  const handleCarDropdownChange = (e) => {
    const carId = e.target.value;
    setSelectedCar(carId);

    // Find the car object by ID
    const selectedCarObj = carsList.find(car => car && String(car.id) === String(carId));
    if (selectedCarObj) {
      // Update form data and custom car name
      setCustomCarName(selectedCarObj.car_name);
      setFormData(prevData => ({
        ...prevData,
        car_name: selectedCarObj.car_name,
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        car_name: "",
      }));
    }

    // Reset dependent fields
    setSelectedMaker("");
    setSelectedModel("");
    setFormData(prevData => ({
      ...prevData,
      maker_name: "",
      model_name: "",
    }));
  };

  const handleMakerDropdownChange = (e) => {
    const makerId = e.target.value;
    setSelectedMaker(makerId);

    // Find the maker object by ID
    const selectedMakerObj = makersList.find(maker => maker && String(maker.id) === String(makerId));
    if (selectedMakerObj) {
      // Update form data and custom maker name
      setCustomMakerName(selectedMakerObj.maker_name);
      setFormData(prevData => ({
        ...prevData,
        maker_name: selectedMakerObj.maker_name,
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        maker_name: "",
      }));
    }

    // Reset model
    setSelectedModel("");
    setFormData(prevData => ({
      ...prevData,
      model_name: "",
    }));
  };

  const handleModelDropdownChange = (e) => {
    const modelId = e.target.value;
    setSelectedModel(modelId);

    // Find the model object by ID
    const selectedModelObj = modelsList.find(model => model && String(model.id) === String(modelId));
    if (selectedModelObj) {
      // Update form data and custom model name
      setCustomModelName(selectedModelObj.model_name);
      setFormData(prevData => ({
        ...prevData,
        model_name: selectedModelObj.model_name,
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        model_name: "",
      }));
    }
  };

  // Modal handlers
  const handleCloseModal = () => {
    setShowUpdateModal(false);
    setShowAddModal(false);
    setPreviewImages([]);

    // Reset custom input states
    setUseCustomCar(false);
    setUseCustomMaker(false);
    setUseCustomModel(false);
    setCustomCarName("");
    setCustomMakerName("");
    setCustomModelName("");
    setImages([]);
    setVideos([]);
  };

  // Add new car
  const handleAdd = () => {
    // Reset form data
    setFormData({
      car_name: "",
      maker_name: "",
      model_name: "",
      car_type: "",
      engine_capacity: "",
      service_type: "",
      product_code: "",
      head_title: "",
      product_desc: "",
      short_des: "",
      d_title: "",
      d_feature: "",
      prices: [{ plan_name: "", price: "", plan_duration: "" }],
    });

    // Reset dropdowns
    setSelectedCar("");
    setSelectedMaker("");
    setSelectedModel("");

    // Reset custom inputs
    setUseCustomCar(false);
    setUseCustomMaker(false);
    setUseCustomModel(false);
    setCustomCarName("");
    setCustomMakerName("");
    setCustomModelName("");

    // Reset media
    setImages([]);
    setVideos([]);
    setPreviewImages([]);
    
    setShowAddModal(true);
  };

  // Update existing car
  const handleUpdate = (car) => {
    if (!car) {
      console.error("No car data provided");
      return;
    }

    try {
      // Handle pricing data with null checks
      const formattedPrices = Array.isArray(car.prices)
        ? car.prices
            .filter(price => price !== null)
            .map(price => ({
              plan_name: price?.plan_name || "",
              price: price?.price || "",
              plan_duration: price?.plan_duration || "",
            }))
        : [{ plan_name: "", price: "", plan_duration: "" }];

      // Set form data with null checks
      setFormData({
        car_name: car?.cars?.car_name || "",
        car_id: car?.cars?.id || "",
        maker_name: car?.makers?.maker_name || "",
        maker_id: car?.makers?.id || "",
        model_name: car?.models?.model_name || "",
        model_id: car?.models?.id || "",
        car_type: car?.car_type || "",
        engine_capacity: car?.engine_capacity || "",
        service_type: car?.service_type || "",
        product_code: car?.product_code || "",
        head_title: car?.head_title || "",
        product_desc: car?.product_desc || "",
        short_des: car?.short_des || "",
        d_title: car?.descriptions?.[0]?.d_title || "",
        d_feature: car?.descriptions?.[0]?.d_feature || "",
        prices: formattedPrices.length > 0 ? formattedPrices : [{ plan_name: "", price: "", plan_duration: "" }],
      });

      // Set custom names
      setCustomCarName(car?.cars?.car_name || "");
      setCustomMakerName(car?.makers?.maker_name || "");
      setCustomModelName(car?.models?.model_name || "");

      // Set dropdown selections
      if (car?.cars?.id) {
        setSelectedCar(car.cars.id);
        setNeedsDropdownUpdate(true);

        // Wait for makers to load, then set the selected maker
        setTimeout(() => {
          if (car?.makers?.id) {
            setSelectedMaker(car.makers.id);

            // Wait for models to load, then set the selected model
            setTimeout(() => {
              if (car?.models?.id) {
                setSelectedModel(car.models.id);
              }
              setNeedsDropdownUpdate(false);
            }, 300);
          } else {
            setNeedsDropdownUpdate(false);
          }
        }, 300);
      }

      // Handle images - safely filter out null values
      setImages(Array.isArray(car.images) ? car.images.filter(image => image) : []);

      // Handle videos - safely filter out null values
      setVideos(Array.isArray(car.videos) ? car.videos.filter(video => video) : []);

      setSelectedCar(car);
      setShowUpdateModal(true);
    } catch (error) {
      console.error("Error preparing update form:", error);
      toast.error("Failed to load vehicle data for update");
    }
  };

  // Add car submission
  const handleAddSubmit = () => {
    // Check for required fields and dropdown selections
    if (!useCustomCar && selectedCar && !formData.car_name) {
      // Try to get car name from list
      const selectedCarObj = carsList.find(car => car && String(car.id) === String(selectedCar));
      if (selectedCarObj) {
        setFormData(prevData => ({
          ...prevData,
          car_name: selectedCarObj.car_name,
        }));
      }
    }

    if (!useCustomMaker && selectedMaker && !formData.maker_name) {
      // Try to get maker name from list
      const selectedMakerObj = makersList.find(maker => maker && String(maker.id) === String(selectedMaker));
      if (selectedMakerObj) {
        setFormData(prevData => ({
          ...prevData,
          maker_name: selectedMakerObj.maker_name,
        }));
      }
    }

    // Submit with a delay to ensure state updates
    setTimeout(() => {
      handleFormSubmit(`${API_BASE_URL}/new-entries/custom_store`, "POST");
    }, 100);
  };

  // Update car submission
  const handleUpdateSubmit = () => {
    // Check for required fields and dropdown selections
    if (!useCustomCar && selectedCar && !formData.car_name) {
      // Try to get car name from list
      const selectedCarObj = carsList.find(car => car && String(car.id) === String(selectedCar));
      if (selectedCarObj) {
        setFormData(prevData => ({
          ...prevData,
          car_name: selectedCarObj.car_name,
        }));
      }
    }

    if (!useCustomMaker && selectedMaker && !formData.maker_name) {
      // Try to get maker name from list
      const selectedMakerObj = makersList.find(maker => maker && String(maker.id) === String(selectedMaker));
      if (selectedMakerObj) {
        setFormData(prevData => ({
          ...prevData,
          maker_name: selectedMakerObj.maker_name,
        }));
      }
    }

    // Get the car ID
    const carId = selectedCar?.id || selectedCar;
    if (!carId) {
      toast.error("Car ID is missing");
      return;
    }

    // Submit with a delay to ensure state updates
    setTimeout(() => {
      handleFormSubmit(`${API_BASE_URL}/new-entries/update/${carId}`, "PUT");
    }, 100);
  };

  // Main form submission function
  const handleFormSubmit = async (url, method) => {
    try {
      // Validate form data
      if (!validateForm()) {
        return;
      }
      
      setFormLoading(true);
      const formDataToSubmit = new FormData();
      
      // If update method, add method override for Laravel
      if (method === "PUT") {
        formDataToSubmit.append("_method", "PUT");
      }
      
      // Prepare car/maker/model data
      let carName = useCustomCar ? customCarName : formData.car_name;
      let makerName = useCustomMaker ? customMakerName : formData.maker_name;
      let modelName = useCustomModel ? customModelName : formData.model_name;
      
      const carId = !useCustomCar ? selectedCar : "";
      const makerId = !useCustomMaker ? selectedMaker : "";
      const modelId = !useCustomModel ? selectedModel : "";
      
      // Add to form data
      formDataToSubmit.append("car_name", carName.trim());
      formDataToSubmit.append("car_id", carId || "");
      formDataToSubmit.append("maker_name", makerName.trim());
      formDataToSubmit.append("maker_id", makerId || "");
      formDataToSubmit.append("model_name", modelName.trim() || "");
      formDataToSubmit.append("model_id", modelId || "");
      
      // Add basic fields
      const textFields = [
        "car_type", "engine_capacity", "service_type", "product_code",
        "head_title", "product_desc", "short_des", "d_title", "d_feature"
      ];
      
      textFields.forEach(field => {
        formDataToSubmit.append(field, (formData[field] || "").trim());
      });
      
      // Add pricing data
      if (Array.isArray(formData.prices)) {
        formData.prices.forEach(price => {
          formDataToSubmit.append("plan_name[]", (price?.plan_name || "").trim());
          formDataToSubmit.append("prices[]", (price?.price || "").trim());
          formDataToSubmit.append("plan_duration[]", (price?.plan_duration || "").trim());
        });
      }
      
      // Add image files
      if (images && images.length > 0) {
        // Filter valid image files
        const validImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
        const validImages = images.filter(
          image => image instanceof File && validImageTypes.includes(image.type)
        );
        
        validImages.forEach(image => {
          formDataToSubmit.append("images[]", image);
        });
      }
      
      // Add video files
      if (videos && videos.length > 0) {
        // Filter valid video files
        const validVideoTypes = ["video/mp4", "video/mov", "video/avi", "video/wmv"];
        const validVideos = videos.filter(
          video => video instanceof File && validVideoTypes.includes(video.type)
        );
        
        validVideos.forEach(video => {
          formDataToSubmit.append("videos[]", video);
        });
      }
      
      // Send the request
      const response = await fetch(url, {
        method: method === "PUT" ? "POST" : method, // For PUT we use POST with _method override
        body: formDataToSubmit,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (data.errors) {
          // Display validation errors
          Object.entries(data.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach(message => {
                toast.error(`${field}: ${message}`);
              });
            }
          });
        } else {
          throw new Error(data.message || "Failed to submit data");
        }
      } else {
        // Success
        toast.success(method === "POST" ? "Vehicle added successfully!" : "Vehicle updated successfully!");
        fetchData();
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit data. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  // Form validation
  const validateForm = () => {
    // Check car name
    if (!formData.car_name && !customCarName) {
      toast.error("Car name is required");
      return false;
    }
    
    // Check maker name
    if (!formData.maker_name && !customMakerName) {
      toast.error("Maker name is required");
      return false;
    }
    
    // Validate images
    const validImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    if (images.some(img => img instanceof File)) {
      const validImages = images.filter(
        image => image instanceof File && validImageTypes.includes(image.type)
      );
      
      if (validImages.length === 0) {
        toast.error("No valid images found. Only JPEG, PNG, JPG, and GIF formats are allowed.");
        return false;
      }
    }
    
    // Validate videos
    const validVideoTypes = ["video/mp4", "video/mov", "video/avi", "video/wmv"];
    if (videos.some(vid => vid instanceof File)) {
      const validVideos = videos.filter(
        video => video instanceof File && validVideoTypes.includes(video.type)
      );
      
      if (validVideos.length === 0) {
        toast.error("No valid videos found. Only MP4, MOV, AVI, and WMV formats are allowed.");
        return false;
      }
    }
    
    return true;
  };

  // Truncate long text for display
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "N/A";
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Render function for pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <Pagination className="justify-content-center mt-3">
        <Pagination.First
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        />
        
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else {
            if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
          }
          
          return (
            <Pagination.Item
              key={pageNum}
              active={currentPage === pageNum}
              onClick={() => setCurrentPage(pageNum)}
            >
              {pageNum}
            </Pagination.Item>
          );
        })}
        
        <Pagination.Next
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <div className="admin-dashboard d-flex flex-column min-vh-100">
      <ToastContainer position="top-right" autoClose={3000} />
      <AdminHeader />
      
      <div className="d-flex flex-grow-1">
        <Sidebar />
        
        <main className="flex-grow-1 bg-light">
          <Container fluid className="py-4">
            <Card className="shadow-sm border-0 mb-4">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 font-weight-bold text-primary">
                  <FaCar className="me-2" /> Vehicle Inventory Management
                </h5>
                
                <Button variant="primary" onClick={handleAdd}>
                  <FaPlus className="me-2" /> Add New Vehicle
                </Button>
              </Card.Header>
              
              <Card.Body className="p-3">
                {error && (
                  <Alert variant="danger" className="mb-4">
                    <FaExclamationTriangle className="me-2" /> {error}
                  </Alert>
                )}
                
                <Form className="mb-4" onSubmit={handleSearchSubmit}>
                  <Row>
                    <Col md={8} lg={9}>
                      <InputGroup>
                        <InputGroup.Text>
                          <FaSearch />
                        </InputGroup.Text>
                        <FormControl
                          placeholder="Search by car name, maker, model, type, etc."
                          value={searchInput}
                          onChange={handleSearchInputChange}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={4} lg={3}>
                      <div className="d-flex">
                        <Button type="submit" variant="outline-primary" className="me-2 flex-grow-1">
                          Search
                        </Button>
                        {searchTerm && (
                          <Button onClick={clearSearch} variant="outline-secondary" className="flex-grow-1">
                            Clear
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Form>
                
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading vehicles...</p>
                  </div>
                ) : displayedCars.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Vehicle Details</th>
                          <th>Specifications</th>
                          <th>Product Info</th>
                          <th>Pricing</th>
                          <th>Media</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedCars.map(car => (
                          <tr key={car.id}>
                            <td>
                              <div className="d-flex flex-column">
                                <span className="fw-bold">{car?.cars?.car_name || "N/A"}</span>
                                <span>{car?.makers?.maker_name || "N/A"}</span>
                                <small className="text-muted">{car?.models?.model_name || "N/A"}</small>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span><strong>Type:</strong> {car.car_type || "N/A"}</span>
                                <span><strong>Engine:</strong> {car.engine_capacity || "N/A"}</span>
                                <span><strong>Service:</strong> {car.service_type || "N/A"}</span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <span><strong>Code:</strong> {car.product_code || "N/A"}</span>
                                <span><strong>Title:</strong> {car.head_title || "N/A"}</span>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {car.short_des || "No description"}
                                    </Tooltip>
                                  }
                                >
                                  <small className="text-muted">
                                    {truncateText(car.short_des, 40) || "N/A"}
                                  </small>
                                </OverlayTrigger>
                              </div>
                            </td>
                            <td>
                              {Array.isArray(car.prices) && car.prices.length > 0 ? (
                                <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                                  {car.prices.map((price, i) => 
                                    price !== null ? (
                                      <div key={i} className="mb-2">
                                        <Badge bg="info" className="me-2">{price?.plan_name || "N/A"}</Badge>
                                        <Badge bg="success">${price?.price || "0"}</Badge>
                                        <small className="d-block text-muted">{price?.plan_duration || "N/A"}</small>
                                      </div>
                                    ) : null
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">No pricing</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex">
                                <div className="me-2">
                                  <Badge bg="primary" className="d-block mb-1">
                                    {Array.isArray(car.images) ? car.images.filter(Boolean).length : 0} Images
                                  </Badge>
                                  
                                  {Array.isArray(car.images) && car.images.length > 0 && (
                                    <OverlayTrigger
                                      placement="left"
                                      overlay={
                                        <Tooltip>
                                          <div className="text-center mb-2">Available Images</div>
                                          <div className="d-flex flex-wrap justify-content-center" style={{ maxWidth: "200px" }}>
                                            {car.images.filter(Boolean).map((img, i) => (
                                              <div key={i} className="m-1">
                                                <img 
                                                  src={`https://motor-easy.clickbooster.io/assets/entryimages/${img.image}`}
                                                  alt={`Car ${i + 1}`}
                                                  style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                                  className="rounded"
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </Tooltip>
                                      }
                                    >
                                      <span className="text-primary cursor-pointer">
                                        <FaEye size={14} />
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                </div>
                                
                                <div>
                                  <Badge bg="info" className="d-block mb-1">
                                    {Array.isArray(car.videos) ? car.videos.filter(Boolean).length : 0} Videos
                                  </Badge>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleUpdate(car)}
                                  title="Edit Vehicle"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleShowDeleteConfirm(car)}
                                  title="Delete Vehicle"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    
                    {renderPagination()}
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <FaExclamationTriangle size={48} className="text-muted mb-3" />
                    <h5>No vehicles found</h5>
                    <p className="text-muted">
                      {searchTerm ? "No vehicles match your search criteria." : "There are no vehicles available."}
                    </p>
                    {searchTerm && (
                      <Button variant="outline-primary" onClick={clearSearch}>
                        Clear Search
                      </Button>
                    )}
                  </div>
                )}
              </Card.Body>
              
              {displayedCars.length > 0 && (
                <Card.Footer className="bg-white border-0 px-4 py-3">
                  <Row className="align-items-center">
                    <Col>
                      <small className="text-muted">
                        Showing {filteredCars.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredCars.length)} of {filteredCars.length} vehicles
                      </small>
                    </Col>
                  </Row>
                </Card.Footer>
              )}
            </Card>
          </Container>
        </main>
      </div>
      
      {/* Vehicle Form Modal */}
      <Modal
        show={showUpdateModal || showAddModal}
        onHide={handleCloseModal}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            {showAddModal ? (
              <><FaPlus className="me-2" /> Add New Vehicle</>
            ) : (
              <><FaEdit className="me-2" /> Edit Vehicle</>
            )}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="p-0">
          <Tabs defaultActiveKey="basic" className="mb-3 px-3 pt-3">
            <Tab eventKey="basic" title={<span><FaCar className="me-2" />Basic Info</span>}>
              <div className="px-3 pb-3">
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formCarSelect" className="mb-3">
                      <Form.Label>Car Type</Form.Label>
                      <InputGroup>
                        <InputGroup.Checkbox
                          checked={useCustomCar}
                          onChange={toggleCustomCar}
                          aria-label="Use custom car name"
                        />
                        {useCustomCar ? (
                          <Form.Control
                            type="text"
                            placeholder="Enter custom car name"
                            value={customCarName}
                            onChange={handleCustomCarNameChange}
                          />
                        ) : (
                          <Form.Select
                            value={selectedCar || ""}
                            onChange={handleCarDropdownChange}
                            disabled={dataLoading}
                          >
                            <option value="">Select Car</option>
                            {carsList.map(car => 
                              car ? (
                                <option key={car.id} value={car.id}>
                                  {car.car_name}
                                </option>
                              ) : null
                            )}
                          </Form.Select>
                        )}
                        {dataLoading && (
                          <InputGroup.Text>
                            <Spinner animation="border" size="sm" />
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                      <Form.Text className="text-muted">
                        {useCustomCar ? "Using custom car name" : "Select from existing cars or check the box to enter a custom name"}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group controlId="formMakerSelect" className="mb-3">
                      <Form.Label>Manufacturer</Form.Label>
                      <InputGroup>
                        <InputGroup.Checkbox
                          checked={useCustomMaker}
                          onChange={toggleCustomMaker}
                          aria-label="Use custom maker name"
                        />
                        {useCustomMaker ? (
                          <Form.Control
                            type="text"
                            placeholder="Enter custom maker name"
                            value={customMakerName}
                            onChange={handleCustomMakerNameChange}
                          />
                        ) : (
                          <Form.Select
                            value={selectedMaker || ""}
                            onChange={handleMakerDropdownChange}
                            disabled={(!selectedCar && !useCustomCar) || dataLoading}
                          >
                            <option value="">Select Maker</option>
                            {makersList.map(maker => 
                              maker ? (
                                <option key={maker.id} value={maker.id}>
                                  {maker.maker_name}
                                </option>
                              ) : null
                            )}
                          </Form.Select>
                        )}
                        {dataLoading && (
                          <InputGroup.Text>
                            <Spinner animation="border" size="sm" />
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                      <Form.Text className="text-muted">
                        {useCustomMaker ? "Using custom maker name" : "Select from existing manufacturers or check the box to enter a custom name"}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formModelSelect" className="mb-3">
                      <Form.Label>Model</Form.Label>
                      <InputGroup>
                        <InputGroup.Checkbox
                          checked={useCustomModel}
                          onChange={toggleCustomModel}
                          aria-label="Use custom model name"
                        />
                        {useCustomModel ? (
                          <Form.Control
                            type="text"
                            placeholder="Enter custom model name"
                            value={customModelName}
                            onChange={handleCustomModelNameChange}
                          />
                        ) : (
                          <Form.Select
                            value={selectedModel || ""}
                            onChange={handleModelDropdownChange}
                            disabled={(!selectedCar && !useCustomCar) || (!selectedMaker && !useCustomMaker) || dataLoading}
                          >
                            <option value="">Select Model</option>
                            {modelsList.map(model => 
                              model ? (
                                <option key={model.id} value={model.id}>
                                  {model.model_name}
                                </option>
                              ) : null
                            )}
                          </Form.Select>
                        )}
                        {dataLoading && (
                          <InputGroup.Text>
                            <Spinner animation="border" size="sm" />
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                      <Form.Text className="text-muted">
                        {useCustomModel ? "Using custom model name" : "Select from existing models or check the box to enter a custom name"}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group controlId="formCarType" className="mb-3">
                      <Form.Label>Car Body Type</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Sedan, SUV, Hatchback"
                        name="car_type"
                        value={formData.car_type || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formEngineCapacity" className="mb-3">
                      <Form.Label>Engine Capacity</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. 2.0L, 1500cc"
                        name="engine_capacity"
                        value={formData.engine_capacity || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group controlId="formServiceType" className="mb-3">
                      <Form.Label>Service Type</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Warranty, Inspection"
                        name="service_type"
                        value={formData.service_type || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formProductCode" className="mb-3">
                      <Form.Label>Product Code</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter product code"
                        name="product_code"
                        value={formData.product_code || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group controlId="formHeadTitle" className="mb-3">
                      <Form.Label>Head Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter head title"
                        name="head_title"
                        value={formData.head_title || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Tab>
            
            <Tab eventKey="descriptions" title={<span><FaInfoCircle className="me-2" />Descriptions</span>}>
              <div className="px-3 pb-3">
                <Form.Group controlId="formProductDesc" className="mb-3">
                  <Form.Label>Product Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Enter detailed product description"
                    name="product_desc"
                    value={formData.product_desc || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                
                <Form.Group controlId="formShortDes" className="mb-3">
                  <Form.Label>Short Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Enter brief summary"
                    name="short_des"
                    value={formData.short_des || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
                
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formDTitle" className="mb-3">
                      <Form.Label>Car Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter car title"
                        name="d_title"
                        value={formData.d_title || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group controlId="formDFeature" className="mb-3">
                      <Form.Label>Car Feature</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter car feature"
                        name="d_feature"
                        value={formData.d_feature || ""}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Tab>
            
            <Tab eventKey="pricing" title={<span><FaDollarSign className="me-2" />Pricing</span>}>
              <div className="px-3 pb-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Pricing Plans</h6>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => addArrayField("prices")}
                  >
                    <FaPlus className="me-1" /> Add Plan
                  </Button>
                </div>
                
                {formData.prices && formData.prices.map((price, index) => (
                  <Card key={index} className="mb-3 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Plan #{index + 1}</h6>
                        {formData.prices.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeArrayField("prices", index)}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                      
                      <Row>
                        <Col md={4}>
                          <Form.Group controlId={`formPlanName${index}`} className="mb-3">
                            <Form.Label>Plan Name</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g. Basic, Premium"
                              name="plan_name"
                              value={price?.plan_name || ""}
                              onChange={(e) => handleArrayChange(e, index, "prices")}
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={4}>
                          <Form.Group controlId={`formPlanDuration${index}`} className="mb-3">
                            <Form.Label>Duration</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g. 1 year, 6 months"
                              name="plan_duration"
                              value={price?.plan_duration || ""}
                              onChange={(e) => handleArrayChange(e, index, "prices")}
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={4}>
                          <Form.Group controlId={`formPrice${index}`} className="mb-3">
                            <Form.Label>Price</Form.Label>
                            <InputGroup>
                              <InputGroup.Text>$</InputGroup.Text>
                              <Form.Control
                                type="text"
                                placeholder="0.00"
                                name="price"
                                value={price?.price || ""}
                                onChange={(e) => handleArrayChange(e, index, "prices")}
                              />
                            </InputGroup>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Tab>
            
            <Tab eventKey="media" title={<span><FaImage className="me-2" />Media</span>}>
              <div className="px-3 pb-3">
                {/* Existing Images */}
                {images && images.length > 0 && !showAddModal && (
                  <div className="mb-4">
                    <h6 className="mb-3">Current Images</h6>
                    <div className="d-flex flex-wrap gap-2 p-3 border rounded bg-light">
                      {images.map((imageObj, index) => (
                        imageObj && imageObj.image ? (
                          <div key={index} className="position-relative">
                            <Image
                              src={`https://motor-easy.clickbooster.io/assets/entryimages/${imageObj.image}`}
                              alt={`Image ${index + 1}`}
                              thumbnail
                              style={{ width: "100px", height: "100px", objectFit: "cover" }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveExistingMedia("Image", imageObj.id)}
                              className="position-absolute top-0 end-0 rounded-circle p-0"
                              style={{ width: "24px", height: "24px" }}
                            >
                              <FaTimes size={12} />
                            </Button>
                          </div>
                        ) : null
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Existing Videos */}
                {videos && videos.length > 0 && !showAddModal && (
                  <div className="mb-4">
                    <h6 className="mb-3">Current Videos</h6>
                    <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light">
                      {videos.map((videoObj, index) => (
                        videoObj && videoObj.video ? (
                          <div key={index} className="position-relative">
                            <video
                              src={`https://motor-easy.clickbooster.io/assets/entryvideos/${videoObj.video}`}
                              controls
                              style={{ width: "200px", height: "120px", objectFit: "cover" }}
                              className="rounded"
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveExistingMedia("Video", videoObj.id)}
                              className="position-absolute top-0 end-0 rounded-circle p-0"
                              style={{ width: "24px", height: "24px" }}
                            >
                              <FaTimes size={12} />
                            </Button>
                          </div>
                        ) : null
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Upload New Images */}
                <Form.Group controlId="formImages" className="mb-4">
                  <Form.Label>
                    <FaImage className="me-2" />
                    {images && images.length > 0 && !showAddModal ? "Add More Images" : "Upload Images"}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    name="images"
                    onChange={(e) => handleFileChange(e, setImages)}
                    accept=".jpg,.jpeg,.png,.gif"
                  />
                  <Form.Text className="text-muted">
                    Accepted formats: JPEG, PNG, JPG, GIF
                  </Form.Text>
                  
                  {/* Image Previews */}
                  {previewImages && previewImages.length > 0 && (
                    <div className="mt-3">
                      <h6 className="mb-2">Preview</h6>
                      <div className="d-flex flex-wrap gap-2 p-3 border rounded bg-light">
                        {previewImages.map((url, index) => (
                          <Image
                            key={index}
                            src={url}
                            alt={`Preview ${index + 1}`}
                            thumbnail
                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </Form.Group>
                
                {/* Upload New Videos */}
                <Form.Group controlId="formVideos" className="mb-3">
                  <Form.Label>
                    <FaVideo className="me-2" />
                    {videos && videos.length > 0 && !showAddModal ? "Add More Videos" : "Upload Videos"}
                  </Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    name="videos"
                    onChange={(e) => handleFileChange(e, setVideos)}
                    accept=".mp4,.mov,.avi,.wmv"
                  />
                  <Form.Text className="text-muted">
                    Accepted formats: MP4, MOV, AVI, WMV
                  </Form.Text>
                </Form.Group>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={handleCloseModal} disabled={formLoading}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          
          <Button
            variant="primary"
            onClick={showAddModal ? handleAddSubmit : handleUpdateSubmit}
            disabled={formLoading}
          >
            {formLoading ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" /> Saving...</>
            ) : showAddModal ? (
              <><FaPlus className="me-2" /> Add Vehicle</>
            ) : (
              <><FaSave className="me-2" /> Update Vehicle</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="mb-3 text-danger">
            <FaExclamationTriangle size={48} />
          </div>
          <h5>Are you sure you want to delete this vehicle?</h5>
          {itemToDelete && (
            <p className="text-muted mb-0">
              <strong>{itemToDelete?.cars?.car_name || ""}</strong> {itemToDelete?.makers?.maker_name || ""} {itemToDelete?.models?.model_name || ""}
            </p>
          )}
          <p className="text-danger mt-3">
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" /> Deleting...</>
            ) : (
              "Delete Vehicle"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AllProducts;